export type VenueCustomerProfileFieldsLayout = 'dialog' | 'signUp';

export interface VenueInnValidationState {
  error: string;
  pending: boolean;
  /** ИНН, успешно проверенный через validateInn (только цифры). */
  validatedInn?: string;
}

/** Сообщение об ошибке ИНН из ответа API (validateInn или регистрация). */
export function extractBookingInnError(err: unknown): string {
  if (!err || typeof err !== 'object') {
    return 'Некорректный ИНН';
  }
  const o = err as Record<string, unknown>;
  const failures = o.failures as Record<string, string[] | undefined> | undefined;
  const innFailures = failures?.inn;
  if (Array.isArray(innFailures) && innFailures.length) {
    const msg = String(innFailures[0]).trim();
    if (msg) {
      return msg;
    }
  }
  const message = o.message ?? o.Message ?? o.error ?? o.title;
  if (message != null && String(message).trim() !== '') {
    const text = String(message).trim();
    if (text !== 'Ошибка валидации') {
      return text;
    }
  }
  return 'Некорректный ИНН';
}

export function hasVenueFacilitiesData(values: Record<string, unknown>): boolean {
  for (const key of VENUE_FACILITY_OPTION_KEYS) {
    if (String(values[`facility_${key}_level`] ?? '').trim()) {
      return true;
    }
    if (String(values[`facility_${key}_notes`] ?? '').trim()) {
      return true;
    }
  }
  if (values.maxAudienceCapacity != null && values.maxAudienceCapacity !== '') {
    return true;
  }
  if (values.stageAreaSquareMeters != null && values.stageAreaSquareMeters !== '') {
    return true;
  }
  if (String(values.timeLimit ?? '').trim()) {
    return true;
  }
  return false;
}

export function validateVenueSignUpForm(
  values: Record<string, unknown>,
  innState: { error: string; pending: boolean; validatedInn: string }
): string | null {
  const innDigits = String(values.inn ?? '').replace(/\D/g, '');
  const innFormatValid = /^\d{10}$|^\d{12}$/.test(innDigits);
  const innValidated =
    innFormatValid && !innState.error && innDigits === innState.validatedInn;

  if (innState.pending && !innValidated) {
    return 'Дождитесь завершения проверки ИНН.';
  }
  if (innState.error) {
    return innState.error;
  }
  if (!String(values.venueName ?? '').trim()) {
    return 'Укажите название организации или заведения.';
  }
  if (!innFormatValid) {
    return 'Укажите корректный ИНН (10 цифр для организации или 12 для ИП).';
  }
  if (!innValidated) {
    return 'Подтвердите ИНН: введите номер и перейдите к следующему полю.';
  }
  if (!String(values.venueTypeTag ?? '').trim()) {
    return 'Укажите тег типа площадки.';
  }
  if (!Array.isArray(values.preferredGenres) || !values.preferredGenres.length) {
    return 'Выберите хотя бы один жанр.';
  }
  if (!Array.isArray(values.preferredPerformerTypes) || !values.preferredPerformerTypes.length) {
    return 'Выберите хотя бы один тип исполнителя.';
  }
  if (!String(values.addressFullAddress ?? '').trim()) {
    return 'Укажите полный адрес площадки.';
  }
  if (!hasVenueFacilitiesData(values)) {
    return 'Укажите условия и возможности площадки (хотя бы один параметр).';
  }
  return null;
}

export const VENUE_FACILITY_OPTION_KEYS = [
  'soundEquipment',
  'lightingEquipment',
  'microphones',
  'backline',
  'soundEngineer',
  'lightingEngineer',
  'technicianSupport',
  'hostOrMc',
  'dressingRoom',
  'backstageArea',
  'equipmentStorage',
  'parking',
  'stagePowerAccess',
  'wiFiOnStage',
  'paysTransfer',
  'paysAccommodation',
  'providesFood',
  'providesFee',
  'hasProjectorOrLed',
  'canRecordOrStream',
  'hasMediaStaff'
] as const;

export type VenueFacilityOptionKey = (typeof VENUE_FACILITY_OPTION_KEYS)[number];

export const VENUE_FACILITY_SUPPORT_LEVELS = [
  { value: 'NotAvailable', name: 'Недоступно' },
  { value: 'AvailableForExtraFee', name: 'За плату' },
  { value: 'AvailableForFree', name: 'Бесплатно' }
];

export function venueFacilityLabel(key: string): string {
  const labels: Record<string, string> = {
    soundEquipment: 'Звуковое оборудование',
    lightingEquipment: 'Световое оборудование',
    microphones: 'Микрофоны',
    backline: 'Backline',
    soundEngineer: 'Звукорежиссер',
    lightingEngineer: 'Светорежиссер',
    technicianSupport: 'Техперсонал',
    hostOrMc: 'Ведущий/MC',
    dressingRoom: 'Гримерка',
    backstageArea: 'Бэкстейдж',
    equipmentStorage: 'Хранение оборудования',
    parking: 'Парковка',
    stagePowerAccess: 'Питание сцены',
    wiFiOnStage: 'Wi-Fi на сцене',
    paysTransfer: 'Оплата трансфера',
    paysAccommodation: 'Оплата проживания',
    providesFood: 'Питание',
    providesFee: 'Гонорар',
    hasProjectorOrLed: 'Проектор/LED',
    canRecordOrStream: 'Запись/стрим',
    hasMediaStaff: 'Медиа-персонал'
  };
  return labels[key] || key;
}
